/**
 * Owner: Masa Bayu
 * File: dropcap-line-painted-face-object.tsx
 * Date:Time: 2026-10-15 08:20
 * Description: R11.4 v2 dropcap · LINE-02 join → LiveObject.
 */

import { Fragment, type ReactElement } from 'react';
import type { TipDropCapPair } from '@qx/qx-layout-write';
import { isSegmentsJoinOk } from '@qx/qx-line-paint';
import type { AssetLibrary, TextElement } from '../extract/spsu-types';
import { LinePaintedFaceObject } from './line-painted-face-object';
import { LiveObject } from './qx-editor-page-face-objects';

type Props = {
  readonly pair: TipDropCapPair;
  readonly tipElements: ReadonlyMap<string, TextElement>;
  readonly assets: AssetLibrary;
  readonly page: { readonly width: number; readonly height: number };
  readonly editable?: boolean;
  readonly onTextChange?: (elementId: string, newContent: string) => void;
};

/**
 * Dropcap pair as discrete L2 lines (letter plate + flow plates).
 * Element without linePaint → LiveObject v1 fallback (fail-closed · no invent gaps).
 */
export function DropCapLinePaintedFaceObject({
  pair,
  tipElements,
  assets,
  page,
  editable = false,
  onTextChange,
}: Props): ReactElement {
  const ids = [pair.letterId, pair.hostId, ...pair.absorbIds, ...pair.flowIds];
  const seen = new Set<string>();
  const nodes: ReactElement[] = [];

  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    const el = tipElements.get(id);
    if (!el) continue;
    if (
      el.linePaint !== undefined &&
      isSegmentsJoinOk(el.content, el.linePaint.segments)
    ) {
      nodes.push(
        <LinePaintedFaceObject
          key={`v2-dc-${id}`}
          element={el}
          assets={assets}
          page={page}
          editable={editable}
          {...(onTextChange ? { onTextChange } : {})}
        />,
      );
    } else {
      nodes.push(
        <LiveObject
          key={`v2-dc-fallback-${id}`}
          element={el}
          assets={assets}
          editable={editable}
          {...(onTextChange ? { onTextChange } : {})}
        />,
      );
    }
  }

  return (
    <Fragment>
      <div
        hidden
        data-native-dropcap=""
        data-render-pipe="v2"
        data-dropcap-letter={pair.letterId}
        data-host-id={pair.hostId}
      />
      {nodes}
    </Fragment>
  );
}
